<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto" @click="handleOverlayClick">
        <!-- Grey Overlay -->
        <div class="fixed inset-0 bg-black" :style="{ opacity: overlayOpacity }"></div>

        <!-- Popup Container -->
        <div
          class="flex min-h-full items-center justify-center p-4"
          :class="{ 'pb-[85px]': isIos(), 'pb-[68px]': !isIos() }"
          @click.stop
        >
          <div
            class="relative w-full max-w-md transform overflow-hidden rounded-[24px] shadow-xl transition-all bg-cover bg-center bg-no-repeat"
            :class="popupClass"
            :style="{ backgroundImage: 'url(/assets/absolutely/homepage/chaptersPopUp.webp)' }"
          >
            <!-- Close Button -->
            <img
              src="/assets/absolutely/homepage/cancelWhite.svg"
              v-if="showCloseButton"
              class="absolute right-[64px] top-[30px] z-10 flex items-center justify-center w-[32px] h-[32px] rounded-full shadow-md transition-transform active:scale-95 focus:outline-none"
              @click="close"
            />
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="#2B2926"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <!-- Header -->
            <!-- <div class="px-6 pt-6 pb-4">
              <h2 class="text-[1.5rem] font-bold text-[#2B2926]">{{ title }}</h2>
            </div> -->

            <!-- Episode List -->
            <div class="px-6 pb-6 max-h-[60vh] overflow-y-auto">
              <!-- Empty State -->
              <div v-if="sortedChapters.length === 0" class="py-8 text-center">
                <p class="text-body-1 text-[#2B2926]">{{ emptyMessage }}</p>
              </div>

              <!-- Episode Items -->
              <div
                v-else
                class="flex flex-col h-[480px] justify-center pt-[46px]"
                :class="gapClass"
              >
                <div
                  v-for="chapter in sortedChapters"
                  :key="chapter.id"
                  class="flex items-center px-[44px]"
                >
                  <cta-button
                    :title="''"
                    :label="chapter.chapterName"
                    :block="true"
                    @click="handleChapterClick(chapter.id)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { mapGetters } from 'vuex';
import { isIosNativeOnly } from '@/utils/platform-check';
import CtaButton from './CtaButton.vue';

export default {
  name: 'ChapterListPopup',
  components: {
    CtaButton,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: 'Episodes',
    },
    emptyMessage: {
      type: String,
      default: 'No episodes available yet.',
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    closeOnOverlayClick: {
      type: Boolean,
      default: true,
    },
    overlayOpacity: {
      type: Number,
      default: 0.5,
      validator: value => value >= 0 && value <= 1,
    },
    gap: {
      type: String,
      default: 'gap-3',
      validator: value => ['gap-2', 'gap-3', 'gap-4'].includes(value),
    },
    popupClass: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters('absolutely', ['getChapters', 'isChapterUnlocked']),
    sortedChapters() {
      // getChapters already returns sorted array
      return this.getChapters || [];
    },
    gapClass() {
      return this.gap;
    },
  },
  watch: {
    modelValue(newValue) {
      if (newValue) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
      }
    },
  },
  beforeUnmount() {
    this.unlockBodyScroll();
  },
  methods: {
    isIos() {
      return isIosNativeOnly();
    },
    lockBodyScroll() {
      document.body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      document.body.style.overflow = '';
    },
    close() {
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },
    handleOverlayClick(event) {
      // Close when clicking on overlay (not the popup itself)
      if (this.closeOnOverlayClick) {
        if (event.target === event.currentTarget || event.target.classList.contains('bg-black')) {
          this.close();
        }
      }
    },
    handleChapterClick(chapterId) {
      this.$emit('chapter-click', chapterId);
      this.close();
      this.$router.push(`/absolutely/chapter/${chapterId}`);
    },
  },
};
</script>

<style scoped>
/* Custom scrollbar for episode list */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c3c2c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #9b9997;
}

:deep(.cta-btn) {
  padding-top: 10px;
  padding-bottom: 10px;
}
</style>
